import { tx as translateInterfaceText } from '@/utils/i18n';
import { useLanguage } from '@/hooks/useLanguage';
import React from 'react';
import { Monitor, Tablet, Smartphone } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import type { PreviewDevice } from './DeviceFrame';

const DEVICE_OPTIONS: Array<{
  id: PreviewDevice;
  icon: typeof Monitor;
  label: string;
  tooltip: string;
}> = [
  { id: 'desktop', icon: Monitor, get label() { return translateInterfaceText("Desktop"); }, get tooltip() { return translateInterfaceText("Visualizar no desktop"); } },
  { id: 'tablet', icon: Tablet, get label() { return translateInterfaceText("Tablet"); }, get tooltip() { return translateInterfaceText("Visualizar no tablet"); } },
  { id: 'mobile', icon: Smartphone, get label() { return translateInterfaceText("Mobile"); }, get tooltip() { return translateInterfaceText("Visualizar no celular"); } },
];

interface DevicePreviewToggleProps {
  value: PreviewDevice;
  onChange: (device: PreviewDevice) => void;
  className?: string;
}

/**
 * Seletor de dispositivo (desktop / tablet / mobile) do preview.
 * Segmented control compacto, vive no cabeçalho do card de preview.
 */
export const DevicePreviewToggle: React.FC<DevicePreviewToggleProps> = ({ value, onChange, className }) => {
  useLanguage();
  return (
    <TooltipProvider delayDuration={200}>
      <div
        role="tablist"
        aria-label={translateInterfaceText("Visualizar em dispositivo")}
        className={cn(
          'preview-device-toolbar-wrapper inline-flex items-center justify-center gap-0.5 rounded-xl border border-border bg-muted p-1',
          className
        )}
      >
        {DEVICE_OPTIONS.map(({ id, icon: Icon, label, tooltip }) => {
          const active = value === id;
          return (
            <Tooltip key={id}>
              <TooltipTrigger asChild>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  role="tab"
                  aria-selected={active}
                  aria-label={label}
                  onClick={() => onChange(id)}
                  className={cn(
                    'h-8 min-h-0 w-10 min-w-0 rounded-lg p-0 transition-all duration-200 ease-out focus-visible:ring-1 focus-visible:ring-primary focus-visible:ring-offset-0',
                    active
                      ? 'border border-border bg-card text-primary shadow-sm hover:bg-card hover:text-primary'
                      : 'border border-transparent bg-transparent text-muted-foreground shadow-none hover:bg-card hover:text-foreground'
                  )}
                >
                  <Icon className="w-[17px] h-[17px]" strokeWidth={2} />
                </Button>
              </TooltipTrigger>
              <TooltipContent side="bottom" className="text-xs">
                {tooltip}
              </TooltipContent>
            </Tooltip>
          );
        })}
      </div>
    </TooltipProvider>
  );
};
